import React, { Dispatch, useReducer } from "react";
import { FormattedMessage } from "react-intl";

import { dateFromDayShift, niceDate } from "@/lib/util";

import { Modal } from "react-bootstrap";
import Loader from "@/modules/shared/ui/Loader";
import { getBackend as getBackend2 } from "@/lib/backend/backend2";
import * as t from "@/lib/backend/manufacturing2.generated.types";
import { History } from "history";
import type { InsightAction } from "@/lib/models";
import type { PayloadAction } from "@/store/types";

import "./Insights.scss";

export const LOADING_INSIGHT_ACTIONS = "loading_insight_actions";
export const LOADED_INSIGHT_ACTIONS = "loaded_insight_actions";
export const ERROR_LOADING_INSIGHT_ACTIONS = "error_loading_insight_actions";
export const HIDE_ACTIONS_MODAL = "hide_actions_modal";

export type LoadingInsightActions = PayloadAction<typeof LOADING_INSIGHT_ACTIONS, string>;
export type LoadedInsightActions = PayloadAction<typeof LOADED_INSIGHT_ACTIONS, t.IInsightAction[]>;
export type ErrorLoadingInsightActions = PayloadAction<typeof ERROR_LOADING_INSIGHT_ACTIONS, typeof undefined>;
export type HideActionsModal = PayloadAction<typeof HIDE_ACTIONS_MODAL, typeof undefined>;
export type InsightsActionsModalActionTypes =
    | LoadingInsightActions
    | LoadedInsightActions
    | ErrorLoadingInsightActions
    | HideActionsModal;

type ActionModalProps = {
    history: History;
    insight: t.IEventDataEx;
    line_title: string;
    reportid: string;
    shift_str: string;
    allowed_actions?: InsightAction[];
    onActionSelect?: (action: t.IInsightAction, insight: t.IEventDataEx) => void;
};

type ActionsModalState = {
    insight_actions: t.IInsightAction[];
    actions_error: boolean;
    loading_actions: boolean;
    insight_actions_uuid: string;
};

const initialState: ActionsModalState = {
    actions_error: false,
    loading_actions: false,
    insight_actions: [],
    insight_actions_uuid: "",
};

const actionsModalReducer = (state: ActionsModalState, action: InsightsActionsModalActionTypes): ActionsModalState => {
    switch (action.type) {
        case LOADING_INSIGHT_ACTIONS:
            return {
                ...state,
                actions_error: false,
                loading_actions: true,
                insight_actions_uuid: action.data,
            };
        case LOADED_INSIGHT_ACTIONS:
            return {
                ...state,
                loading_actions: false,
                insight_actions: action.data,
            };
        case ERROR_LOADING_INSIGHT_ACTIONS:
            return {
                ...state,
                insight_actions: [],
                actions_error: true,
                loading_actions: false,
            };
        case HIDE_ACTIONS_MODAL:
            return {
                ...state,
                insight_actions_uuid: "",
            };
        default:
            return state;
    }
};

const showActionsModal = async (
    dispatch: Dispatch<InsightsActionsModalActionTypes>,
    insight: t.IEventDataEx,
    reportid: string
) => {
    try {
        dispatch({ type: LOADING_INSIGHT_ACTIONS, data: insight.uuid });
        const res = await getBackend2().manufacturing.getInsightActions({ insight, report_id: reportid });
        dispatch({ type: LOADED_INSIGHT_ACTIONS, data: res.actions });
    } catch (e) {
        dispatch({ type: ERROR_LOADING_INSIGHT_ACTIONS, data: undefined });
    }
};

const Actions = ({
    actions,
    history,
    insight,
    onActionSelect,
}: {
    actions: t.IInsightAction[];
    history: History;
    insight: t.IEventDataEx;
    onActionSelect?: (action: t.IInsightAction, insight: t.IEventDataEx) => void;
}) => {
    const actions_list = actions.map(action => {
        return (
            <button
                onClick={() => {
                    if (onActionSelect) {
                        onActionSelect(action, insight);
                        return;
                    }

                    history.push(action.url);
                }}
                className="list-group-item list-group-item-action"
                key={action.code}
            >
                {action.description}
            </button>
        );
    });

    return <>{actions_list}</>;
};

const ActionsModal = (props: ActionModalProps) => {
    const { insight, shift_str, line_title, reportid, history, allowed_actions, onActionSelect } = props;

    const [state, dispatch] = useReducer(actionsModalReducer, initialState);

    const { insight_actions: all_insight_actions, actions_error, loading_actions, insight_actions_uuid } = state;

    const firstShiftStart = dateFromDayShift(new Date(), 0).getTime();

    // Show actions only for future insights
    if (insight.ts < firstShiftStart) {
        return null;
    }

    const insight_actions = allowed_actions
        ? all_insight_actions.filter(action => allowed_actions.includes(action.code as InsightAction))
        : all_insight_actions;

    return (
        <>
            <button
                className="btn btn-flat-primary"
                onClick={() => {
                    showActionsModal(dispatch, insight, reportid);
                }}
            >
                <FormattedMessage id="common.actions" defaultMessage="Actions" />
            </button>
            <Modal
                show={insight_actions_uuid === insight.uuid}
                size="lg"
                dialogClassName="modal-dialog-scrollable"
                onHide={() => {
                    dispatch({ type: HIDE_ACTIONS_MODAL, data: undefined });
                }}
            >
                <Modal.Header>
                    <Modal.Title>
                        {line_title}: {insight.title}
                        <br />
                        {niceDate(new Date(insight.ts))}, {shift_str} Shift
                    </Modal.Title>
                    <button
                        type="button"
                        className="close"
                        onClick={() => {
                            dispatch({ type: HIDE_ACTIONS_MODAL, data: undefined });
                        }}
                    >
                        <span aria-hidden="true">×</span>
                        <span className="sr-only">
                            <FormattedMessage id="common.close" defaultMessage="Close" />
                        </span>
                    </button>
                </Modal.Header>
                <Modal.Body>
                    <p>{insight.description}</p>
                    <h5 className="h6 font-weight-normal">
                        <FormattedMessage id="insights.suggested_actions" defaultMessage="Suggested actions" />:
                    </h5>
                    <div className="list-group">
                        {loading_actions && <Loader small={true} />}
                        {!loading_actions && insight_actions.length === 0 && !actions_error && (
                            <p className="p-3 mx-auto">
                                <FormattedMessage id="insights.no_actions" defaultMessage="No actions available" />
                            </p>
                        )}
                        {actions_error && (
                            <p className="p-3 mx-auto">
                                <FormattedMessage
                                    id="insights.actions_error"
                                    defaultMessage="Error retrieving actions"
                                />
                            </p>
                        )}
                        {!loading_actions && (
                            <Actions
                                actions={insight_actions}
                                history={history}
                                insight={insight}
                                onActionSelect={onActionSelect}
                            />
                        )}
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default ActionsModal;
